import React from 'react';
import { PuffLoader } from 'react-spinners';
import { Button, Input, InputTextArea, Label, RadioSet, SelectBox } from 'components';
import {
  registerPolarizationList,
  registerModulationList,
  reedSolomonCodeList,
  registerPCMCodingList,
  registerConvolutionalCodeList
} from './utils';

import { FormData, formatNumber } from 'utils/validation';

interface Props {
  formData: FormData;
  onNext: (data: FormData) => void;
  errors: Partial<Record<keyof FormData, string>>;
  handleInputChange: (field: keyof FormData, value: any) => void;
  isLoading: boolean;
  error: string | null;
  handleFetchTLE: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const XDownLink: React.FC<Props> = ({ 
  formData, 
  errors, 
  handleInputChange, 
  isLoading, 
  error, 
  handleFetchTLE, 
  handleKeyDown 
}) => {
  return (
    <div className="flex flex-col gap-4">
      <Input
        label="Name"
        labelTooltip="This is the name used when searching for a schedule."
        value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)}
        errorMessage={errors.name}
      />
      {/* NORAD + 버튼 + 로더 : 상단 정렬로 고정 */}
      <div className="flex items-start gap-2">
        <div className='flex-1'>
          <Input
            label="NORAD ID"
            labelTooltip="It automatically fills in when you click 'Get TLE' or press Enter."
            value={formData.noradId}
            onChange={(e) => handleInputChange('noradId', e.target.value)}
            onKeyDown={handleKeyDown}
            errorMessage={errors.noradId}
            placeholder="It automatically fills in when you click 'Get TLE' or press Enter."
          />
        </div>
        {/* 버튼: 항상 상단 기준으로 고정 */}
          <Button className="self-start h-8 mt-6" onClick={handleFetchTLE} type="secondary" size="small">
            Get TLE
          </Button>
        {isLoading && (
          <div className='flex items-end justify-center'>
            <PuffLoader size={30} />
          </div>
        )}
      </div>
      {error && <div className="text-red-500">{error}</div>}
      <Input
        label="TLE - Title"
        value={formData.tleTitle}
        onChange={(e) => handleInputChange('tleTitle', e.target.value)}
        errorMessage={errors.tleTitle}
      />
      <Input
        label="TLE - Line 1"
        value={formData.tleFirst}
        onChange={(e) => handleInputChange('tleFirst', e.target.value)}
        errorMessage={errors.tleFirst}
      />
      <Input
        label="TLE - Line 2"
        value={formData.tleSecond}
        onChange={(e) => handleInputChange('tleSecond', e.target.value)}
        errorMessage={errors.tleSecond}
      />
      <Input
        label="Center Frequency (MHz)"
        labelTooltip="Tx : 2025 ~ 2120  Rx : 2200 ~ 2300"
        value={formData.centerFrequency}
        placeholder="Tx : 2025 ~ 2120  Rx : 2200 ~ 2300"
        onChange={(e) => handleInputChange('centerFrequency', formatNumber(e.target.value))}
        errorMessage={errors.centerFrequency}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label="Modulation"
        list={registerModulationList}
        value={formData.modulation}
        onChange={(value) => handleInputChange('modulation', value)}
      />
      <Input
        label="Bit Rate (bps)"
        labelTooltip="Min: 500,000bps   Max: 12,000,000,000bps"
        placeholder="Min: 500,000bps   Max: 12,000,000,000bps"
        value={formData.bitRate}
        onChange={(e) => handleInputChange('bitRate', formatNumber(e.target.value))}
        errorMessage={errors.bitRate}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label="Polarization"
        list={registerPolarizationList}
        value={formData.polarization}
        onChange={(value) => handleInputChange('polarization', value)}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label="Convolutional Code or Derandomization"
        list={registerConvolutionalCodeList}
        value={formData.convolutionalCode}
        onChange={(value) => handleInputChange('convolutionalCode', value)}
      />
      <RadioSet
        label="Reed-Solomon Code (FEC)"
        list={reedSolomonCodeList}
        value={formData.reedSolomonCode}
        onChange={(value) => handleInputChange('reedSolomonCode', value)}
      />
      <Input
        label="Subcarrier Frequency (Hz)"
        labelTooltip="High bandwidth with sub-carrier"
        placeholder="0 to 2,000,000 (in Hz)"
        value={formData.subcarrierFrequency}
        onChange={(e) => handleInputChange('subcarrierFrequency', formatNumber(e.target.value))}
        errorMessage={errors.subcarrierFrequency}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label="PCM Coding"
        list={registerPCMCodingList}
        value={formData.pcmCoding}
        onChange={(value) => handleInputChange('pcmCoding', value)}
      />
      <div className="flex flex-col w-full">
        <div className="flex flex-row">
          <Label
            label="Sync Word"
            htmlFor="Sync Word"
            labelTooltip="Base: 0x1ACFFC1D (32bit, 4byte)"
          />
        </div>
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between items-center gap-1">
            <label htmlFor="framesyncword" className="text-text-secondary font-regular text-body2">0x</label>
            <input
              type="text"
              id="framesyncword"
              className="h-8 flex-1 rounded border bg-transparent px-3 text-body"
              placeholder="1ACFFC1D (32bit, 4byte)"
              value={formData.framesyncword}
              onChange={(e) => handleInputChange('framesyncword', e.target.value)}
            />
          </div>
          {errors.framesyncword && (
            <p className="mt-2 text-red-500 text-body2">{errors.framesyncword}</p>
          )}
        </div>
      </div>
      <Input
        label="Frame Size (bytes)"
        value={formData.framesize}
        onChange={(e) => handleInputChange('framesize', formatNumber(e.target.value))}
        errorMessage={errors.framesize}
      />
      <div className="flex flex-col gap-2">
      <Label
        label={'Synchronization Strategy'}
        htmlFor={'Synchronization Strategy'}
        labelTooltip={'Synchronization Strategy'}
      />
      <div className="grid grid-cols-4 gap-2">
        <Input
          label="SYN"
          type="number"
          value={formData.syn.toString()}
          onChange={(e) => handleInputChange('syn', Number(e.target.value))}
          errorMessage={errors.syn}
        />
        <Input
          label="CTL"
          type="number"
          value={formData.ctl.toString()}
          onChange={(e) => handleInputChange('ctl', Number(e.target.value))}
          errorMessage={errors.ctl}
        />
        <Input
          label="LTS"
          type="number"
          value={formData.lts.toString()}
          onChange={(e) => handleInputChange('lts', Number(e.target.value))}
          errorMessage={errors.lts}
        />
        <Input
          label="SLIP"
          type="number"
          value={formData.slip.toString()}
          onChange={(e) => handleInputChange('slip', Number(e.target.value))}
          errorMessage={errors.slip}
        />
      </div>
      </div>
      <InputTextArea
        label="(Optional) Other"
        value={formData.other}
        onChange={(value) => handleInputChange('other', value)}
      />
    </div>
  );
};

export default XDownLink; 